import { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 grid place-items-center px-5 py-12">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-10">
          <span className="grid place-items-center w-12 h-12 rounded-xl bg-slate-900 text-white mb-6">
            <ShieldCheck className="w-6 h-6" />
          </span>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Admin sign in</h1>
          <p className="mt-2 text-sm text-slate-500">
            This area contains usage analytics and customer lead data. Sign in to continue.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Email</Label>
              <Input
                type="email"
                autoComplete="username"
                data-testid="admin-login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@company.com"
                required
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Password</Label>
              <Input
                type="password"
                autoComplete="current-password"
                data-testid="admin-login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="admin-login-submit"
              className="w-full rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold py-2.5 active:scale-95 transition-transform"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
              Sign in
            </Button>
          </form>
        </div>
        <p className="text-center text-xs text-slate-400 mt-5">Protected area · authorized personnel only</p>
      </motion.div>
    </div>
  );
}
